import { ContentBody } from "@app/components/core/ContentBody/ContentBody"
import { Loader } from "@app/components/core/Loader/Loader"
import { Separator } from "@app/components/core/Separator/Separator"
import SucceedCheckmark from "@app/components/core/SucceedCheckmark"
import Button from "@app/components/move2Library/Button/Button"
import OrderLine from "@app/components/order/OrderLine/OrderLine"
import OrderLines from "@app/components/order/OrderLines/OrderLines"
import { PriceSummary } from "@app/components/order/PriceSummary/PriceSummary"
import Page from "@app/components/pageTypes/Page/Page"
import { Timer } from "@app/modals/ErrorModal"
import { useRaiseError } from "@app/store/actions/errorActions"
import { setFormattedOrder } from "@app/store/actions/orderActions"
import { useGetLoungerTypes, useGetZone } from "@app/store/service/cmsService"
import { useFormatOrder } from "@app/store/service/connectorService"
import { appStore } from "@app/store/store"
import { useDelayLoading } from "@app/utils/hooks/useDelayLoading"
import { flatFormattedOrderForKisok } from "@app/utils/objectUtils"
import { KioskBridge } from "@tecreation/bird5-kiosk-bridge"
import { useSelector } from "@tecreation/js-state-react"
import { useCallback, useEffect, useState } from "react"

const getTransponder = async () => {
	return await KioskBridge.execute("transponder.kiosk.getActive")
}
const removeTransponder = async () => {
	return KioskBridge.execute("transponder.kiosk.close").catch(e => {
		if (e !== "wrn.NoActiveTransponder") {
			throw e
		}
	})
}

const getRelByScanCode = async scanCode => {
	let activeGuest = null
	try {
		await KioskBridge.execute("transponder.kiosk.scan", {
			scanCode: scanCode,
			districtChoiceAfterScan: false,
			objectChoiceAfterScan: false,
		})

		activeGuest = await getTransponder()
	} catch {
		activeGuest = null
	}

	return activeGuest
}

export default function Page3({ onNext, onBack }) {
	const formattedOrder = useSelector("order.formattedOrder")
	const zoneId = useSelector("core.zoneId")
	const [__formatOrder, { isFetching: __formatFetching }] = useFormatOrder()
	const raiseError = useRaiseError()

	const [scanning, setScanning] = useState(false)
	const [relation, setRelation] = useState(null)
	const [isError, setIsError] = useState(false)
	const [isDone, setIsDone] = useState(false)
	const [inProgress, setInProgress] = useState(false)
	const zone = useGetZone(appStore.get("core.zoneId"))
	const formatFetching = useDelayLoading(__formatFetching, 200)

	const { data: loungerTypes } = useGetLoungerTypes({ zoneId }, { skip: !zoneId })

	const pay = useCallback(async () => {
		setInProgress(true)
		try {
			if (formattedOrder.payAmount > relation.balance) {
				throw new Error("Hauskredit nicht ausreichend!")
			}
			let data = flatFormattedOrderForKisok(formattedOrder, undefined)
			await KioskBridge.execute("web.process.order", data)
			await KioskBridge.execute("receipt.kiosk.stop")
			await removeTransponder()
			setIsDone(true)
		} catch (e) {
			raiseError(typeof e === "string" ? new Error(e) : e)
			await removeTransponder()
			setRelation(undefined)
		}
		setInProgress(false)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onNext, formattedOrder, relation])

	useEffect(() => {
		const rev = KioskBridge.onScanCode(async scanCode => {
			setScanning(true)
			try {
				const relation = await getRelByScanCode(scanCode.scanCode)
				setRelation(relation)
				if (!relation) {
					setIsError(true)
				} else {
					setIsError(false)
				}
			} catch {
				setRelation(null)
				setIsError(true)
			}
			setScanning(false)
		})
		return () => rev()
	}, [pay])

	const getMessage = useCallback(() => {
		if (scanning) return "Prüfe Transponder..."
		if (relation) return "Transponder erkannt"
		if (isError)
			return (
				<div>
					<div>Transponder nicht erkannt.</div>
					<div>Versuche es erneut...</div>
				</div>
			)
		if (!relation && !scanning) return "Warte auf Transponder.."
		if (!scanning && relation) return "Buche auf Transponder..."
	}, [scanning, relation, isError])

	useEffect(() => {
		try {
			__formatOrder({})
				.unwrap()
				.then(rsp => {
					setFormattedOrder(rsp, loungerTypes, [], [])
				})
		} catch (e) {
			raiseError(e, undefined, false, true)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const finish = useCallback(async () => {
		let active = await getTransponder()
		if (active) {
			throw new Error("transponder is still active. ")
		}
		onNext()
		appStore.set(state => ({
			order: {
				...state.order,
				formattedOrder: undefined,
				loungers: [],
				loungerPrice: 0,
				objectGroupOptions: [],
			},
			core: {
				...state.core,
				zoneId: undefined,
			},
		}))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	if (formattedOrder && isDone) {
		return (
			<Page>
				<div className="flex flex-center mt-6">
					<SucceedCheckmark size={50} />
				</div>
				<ContentBody scrollable className="flex-1 flex flex-center flex-column mt-3">
					<div className="f00">Buchung erfolgreich!</div>
					<div className="mt-3 w-100 px-12">
						<div className="text-center f3 mb-4">
							<div className="pt-3">Mediumnummer: {relation.transponderId}</div>
						</div>
						<div className="mt-auto">
							<div className="semibold">{zone.data?.title}</div>
							<Separator />
							<OrderLines>
								{formattedOrder.orderLines.map((ol, i) => (
									<OrderLine orderLine={ol} key={i} />
								))}
							</OrderLines>
						</div>
					</div>
				</ContentBody>
				<div className="pt-6 flex flex-center pb-3">
					<Button style="secondary" onClick={finish}>
						Zum Homebildschirm (<Timer seconds={10} onFinish={finish} />)
					</Button>
				</div>
			</Page>
		)
	}

	return (
		<Page className="pb-0">
			<ContentBody className="flex-1 flex flex-column" scrollable>
				{formatFetching && (
					<div className="h-100 w-100 flex flex-center absolute top-0 left-0 color-fg-white" style={{ backgroundColor: "rgba(0,0,0,.5)" }}>
						<Loader show={formatFetching} />
					</div>
				)}

				{formattedOrder && (
					<>
						<div className="f00 text-center mt-3">{getMessage()}</div>
						{relation && (
							<div className="text-center f3">
								<div>Mediumnummer: {relation.transponderId}</div>
							</div>
						)}

						<div className="mt-auto">
							<div className="semibold">{zone.data?.title}</div>
							<Separator />
							<OrderLines>
								{formattedOrder.orderLines.map((ol, i) => (
									<OrderLine orderLine={ol} key={i} />
								))}
							</OrderLines>
							<PriceSummary formattedOrder={formattedOrder} />
						</div>
					</>
				)}
			</ContentBody>

			<div className="w-100 mt-auto flex flex-justify-between p-4">
				<div>
					<Button
						style="outlined"
						className="ml-4 primary"
						onClick={async () => {
							await removeTransponder()
							onBack()
						}}
						disabled={scanning}
					>
						Zurück
					</Button>
				</div>
				<div>
					<Button
						style="secondary"
						className="ml-4 primary"
						onClick={async () => {
							pay()
						}}
						disabled={!relation}
						isLoading={inProgress}
					>
						Auf Transponder buchen
					</Button>
				</div>
			</div>
		</Page>
	)
}
